import React, { useMemo } from 'react';
import { Box, Grid, makeStyles, PropTypes } from '@material-ui/core';
import * as Assets from '@assets';
import { CustomizedTypography } from '@components/Customized';
import AppConfig from '@config';
import {
  CapaQuotationDetailedMaterialType,
  CapaQuotationDetailedPcbAssemblyItem,
  CapaQuotationDetailedPcbFabricationItem,
  CapaQuotationDetailedQuotationItem
} from '@generated/graphql';
import { requestEnumToString } from '@utils/enumToString';
import { colorSet } from '@components/Provider';

interface PurchaseOrderItemsTableProps {
  projectName: string;
  items: (
    | CapaQuotationDetailedPcbFabricationItem
    | CapaQuotationDetailedPcbAssemblyItem
    | CapaQuotationDetailedQuotationItem
  )[];
}

const PurchaseOrderItemsTable = ({
  projectName,
  items
}: PurchaseOrderItemsTableProps) => {
  const classes = useStyles();

  const totalPrice = useMemo(() => {
    return items.reduce(
      (acc, cur) => acc + (cur.cost ? Number(cur.cost) : 0),
      0
    );
  }, [items]);

  // pcb 타입이 아닌 노말일 경우 ''
  const itemType =
    items[0].__typename === 'CAPAQuotationDetailedPcbAssemblyItem'
      ? '조립'
      : items[0].__typename === 'CAPAQuotationDetailedPcbFabricationItem'
      ? '제작'
      : '';

  return (
    <div className={classes.root}>
      <CustomizedTypography variant="h4" fontWeight="500" gutterBottoms={16}>
        {itemType} 견적정보
      </CustomizedTypography>
      <Box className={classes.tableRoot}>
        <Grid container className={classes.tableHeader} wrap="nowrap">
          {[
            { title: '항목', width: '448px', align: 'left' },
            { title: '금액', width: '152px', align: 'right' }
          ].map((v, i) => (
            <Box key={i} maxWidth={v.width} width="100%">
              <CustomizedTypography
                align={v.align as PropTypes.Alignment}
                variant="subtitle2"
                color="textSecondary"
              >
                {v.title}
              </CustomizedTypography>
            </Box>
          ))}
        </Grid>

        {items.map((item, i) => {
          return (
            <Grid container key={i} className={classes.tableData} wrap="nowrap">
              <Grid item>
                <CustomizedTypography
                  variant="body2"
                  color="textSecondary"
                  className="index"
                >
                  {i + 1}
                </CustomizedTypography>
                {itemType !== '조립' && !item.fileId?.includes(projectName) && (
                  <img
                    alt="thumbnail"
                    src={
                      item.fileId
                        ? AppConfig.fileApiUrl +
                          `/cloud/` +
                          item.fileId +
                          '.png'
                        : Assets.DefaultThumbnail
                    }
                    onError={(e) => {
                      e.currentTarget.src = Assets.DefaultThumbnail;
                    }}
                    className="thumbnail"
                  />
                )}
                <Box marginLeft={2} maxWidth={'calc(100% - 105px)'}>
                  <CustomizedTypography
                    variant="body1"
                    color="textPrimary"
                    gutterBottoms={4}
                    className="fileName"
                  >
                    {item.fileName}
                  </CustomizedTypography>

                  {itemType === '' && (
                    <CustomizedTypography
                      className="material"
                      variant="body2"
                      color="textSecondary"
                    >
                      {`재료: ${requestEnumToString(
                        (item as CapaQuotationDetailedQuotationItem)
                          .material as CapaQuotationDetailedMaterialType
                      )}`}
                    </CustomizedTypography>
                  )}

                  <CustomizedTypography
                    variant="body2"
                    color="textSecondary"
                    gutterBottoms={2}
                  >
                    {`수량: ${item.quantity.toLocaleString('kr')}개`}
                  </CustomizedTypography>

                  <CustomizedTypography variant="body2" color="textSecondary">
                    {`단가: ${Number(item.cost / item.quantity).toLocaleString(
                      'kr'
                    )}원`}
                  </CustomizedTypography>
                </Box>
              </Grid>

              <Box maxWidth={94}>
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                  align="right"
                  style={{ wordBreak: 'break-all' }}
                >
                  {'총 ' + Number(item.cost).toLocaleString('kr') + ' 원'}
                </CustomizedTypography>
              </Box>
            </Grid>
          );
        })}
        {itemType !== '' && (
          <div className="file-table-subtotal">
            <CustomizedTypography variant="body1" color="textSecondary">
              {itemType} 총액
            </CustomizedTypography>
            <CustomizedTypography
              variant="body1"
              color="textSecondary"
            >{`총 ${totalPrice.toLocaleString('kr')} 원`}</CustomizedTypography>
          </div>
        )}
      </Box>
    </div>
  );
};

export default PurchaseOrderItemsTable;

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 600,
    width: '100%',
    padding: '16px 24px',
    [theme.breakpoints.down('md')]: {
      padding: '16px'
    }
  },
  tableRoot: {
    '& .file-table-subtotal': {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: 8
    }
  },
  tableHeader: {
    justifyContent: 'space-between',
    borderBottom: `1px solid ${colorSet.gray200}`,
    '& > div': {
      padding: '8px 0px'
    }
  },
  tableData: {
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    height: '100%',
    padding: '21px 0px',
    borderBottom: `1px solid ${colorSet.gray200}`,
    '& > :nth-child(1)': {
      display: 'flex',
      width: 'calc(100% - 100px)',
      '& .index': {
        width: 14
      },
      '& img': {
        marginLeft: 16
      },
      '& .thumbnail': {
        border: `1px solid ${colorSet.gray200}`,
        width: 70,
        height: 70,
        borderRadius: 8
      },
      '& .fileName': {
        maxWidth: 340,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'pre',
        [theme.breakpoints.down('md')]: {
          maxWidth: '100%'
        }
      },
      '& .material': {
        wordBreak: 'break-all',
        [theme.breakpoints.down('md')]: {
          maxWidth: '100%'
        }
      }
    },
    '& > :nth-child(2)': {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      flexDirection: 'column'
    }
  }
}));
